import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import AvatarEditor from 'flarum/forum/components/AvatarEditor';
import UserCard from 'flarum/forum/components/UserCard';
import UserControls from 'flarum/forum/utils/UserControls';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import listItems from 'flarum/common/helpers/listItems';
import humanTime from 'flarum/common/utils/humanTime';
import icon from 'flarum/common/helpers/icon';
import classList from 'flarum/common/utils/classList';
import ItemList from 'flarum/common/utils/ItemList';

import ProfileStats from './ProfileStats';

/**
 * Replaces the core UserCard hero on the profile page with the redesigned
 * layout: a cover band, an overlapping avatar, badges, bio and a stats strip.
 *
 * Only used on UserPage — the hover card in discussions keeps Flarum's own
 * UserCard so the rest of the forum is untouched.
 */
export default class ProfileHero extends Component {
  view() {
    const user = this.attrs.user;

    // Built once: `bio()` pulls its entry out of this list, so the meta line
    // below must render the same instance rather than a fresh one.
    const meta = this.metaItems(user);

    return (
      <div className="ProfileRedesign-hero">
        {this.cover(user)}

        <div className="ProfileRedesign-identity">
          <div className="ProfileRedesign-topRow">
            <div className="ProfileRedesign-avatarWrap">
              {this.attrs.editable ? (
                <AvatarEditor user={user} className="ProfileRedesign-avatar" />
              ) : (
                <div className="ProfileRedesign-avatar">{avatar(user, { loading: 'eager' })}</div>
              )}
              {user.isOnline() && <span className="ProfileRedesign-online" aria-label={app.translator.trans('core.forum.user.online_text')} />}
            </div>

            {this.primaryActions(user)}
          </div>

          <div className="ProfileRedesign-copy">
            <div className="ProfileRedesign-nameRow">
              <h1 className="ProfileRedesign-name">{username(user)}</h1>
              {this.badges(user)}
            </div>

            {this.bio(meta)}

            <ul className="ProfileRedesign-meta">{listItems(meta.toArray())}</ul>
          </div>

          <ProfileStats user={user} />
        </div>
      </div>
    );
  }

  /**
   * Prominent per-user actions, shown beside the avatar.
   *
   * ianm/follow-users publishes its follow button in one of two places
   * depending on its `button-on-profile` setting: spliced into UserCard's vdom
   * when on, or added to `UserControls.userControls` when off. This redesign
   * renders neither, so both paths are checked — the profile button first,
   * since that is the one that opens the follow-level modal.
   */
  primaryActions(user) {
    const actions = [];

    const followButton = this.followButton(user);

    if (followButton) {
      actions.push(followButton);
    } else {
      const menuItems = UserControls.userControls(user, this);

      ['follow', 'unfollow'].forEach((key) => {
        if (menuItems.has(key)) actions.push(menuItems.get(key));
      });
    }

    if (!actions.length) return null;

    return <div className="ProfileRedesign-actions">{actions}</div>;
  }

  /**
   * The follow button, taken from ianm/follow-users rather than rebuilt.
   *
   * That extension renders its profile button by extending `UserCard.view` and
   * splicing into `.UserCard-profile`. Since this redesign never renders a
   * UserCard, the button is produced here by running UserCard's own view for
   * this user off-screen and lifting the button back out of the resulting vdom.
   *
   * Doing it this way keeps the extension's real button — its follow-level
   * modal, labels, and permission checks — instead of a lookalike that would
   * drift as the extension changes. Returns null when the extension is absent
   * or contributes nothing.
   */
  followButton(user) {
    // Cheap bail-out when the extension is not installed at all.
    if (typeof user.canBeFollowed !== 'function') return null;

    let card;

    try {
      const context = this.userCardContext({
        user,
        className: 'UserCard--profileRedesignProbe',
        controlsButtonClassName: 'Button',
      });

      // UserCard.view() calls this.infoItems(), which runs every extension's
      // infoItems extender — work this probe throws away, and which metaItems()
      // has already done for real. Stub it so each extender runs once per
      // render rather than twice.
      context.infoItems = () => new ItemList();

      card = UserCard.prototype.view.call(context);
    } catch (e) {
      console.error('[profile-redesign] UserCard.view threw; the follow button is unavailable.', e);

      return null;
    }

    const button = this.extractFollowButton(card);

    if (!button) return null;

    return this.restyleFollowButton(button, user);
  }

  /**
   * Adjust the extension's button to this design: no icon, and a single
   * "Following" label instead of the per-level wording ("Following discussions"
   * / "Following all") the extension shows.
   *
   * Only presentation is touched — `onclick` is left alone, so the button still
   * opens the extension's own follow-level modal.
   */
  restyleFollowButton(button, user) {
    const following = typeof user.followed === 'function' && user.followed();

    button.attrs = {
      ...button.attrs,
      icon: null,
      className: classList(button.attrs.className, { 'ProfileRedesign-followButton--following': following }),
    };

    if (following) {
      button.children = [app.translator.trans('happydigital-profile-redesign.forum.following')];
    }

    return button;
  }

  /**
   * A stand-in `this` for calling UserCard's own methods.
   *
   * It must inherit from UserCard.prototype, not just carry `attrs`: `view()`
   * calls `this.infoItems()` on itself, so a plain object throws
   * "this.infoItems is not a function" — which, being swallowed by the callers'
   * try/catch, silently removed the follow button and the bio.
   */
  userCardContext(attrs) {
    const context = Object.create(UserCard.prototype);

    context.attrs = attrs;

    return context;
  }

  /**
   * Walk a vdom tree and return the first node carrying the follow button's
   * class, which is how ianm/follow-users marks it.
   */
  extractFollowButton(vdom) {
    if (!vdom || typeof vdom !== 'object') return null;

    if (Array.isArray(vdom)) {
      for (const child of vdom) {
        const found = this.extractFollowButton(child);
        if (found) return found;
      }

      return null;
    }

    const className = (vdom.attrs && vdom.attrs.className) || '';

    if (typeof className === 'string' && className.indexOf('Button--follow-profile') !== -1) {
      return vdom;
    }

    return this.extractFollowButton(vdom.children);
  }

  /**
   * The cover band. sycho/flarum-profile-cover exposes `cover` on the user
   * model; when that extension is absent or the user has no cover the band
   * falls back to a flat themed strip via CSS.
   */
  cover(user) {
    const coverUrl = typeof user.cover === 'function' ? user.cover() : null;

    return (
      <div
        className={classList('ProfileRedesign-cover', { 'has-image': !!coverUrl })}
        style={coverUrl ? { backgroundImage: `url(${coverUrl})` } : undefined}
      >
        {this.coverButton(user)}
      </div>
    );
  }

  /**
   * The "change cover" control, pinned to the cover's top-right corner.
   *
   * sycho/flarum-profile-cover contributes it to `UserControls.moderationControls`
   * (guarded by its own `canSetProfileCover` check), so it is taken from there
   * rather than rebuilt — that keeps the extension's upload modal and its
   * permission logic. `controlItems()` drops it from the `⋮` menu so it is not
   * offered twice.
   */
  coverButton(user) {
    const items = UserControls.moderationControls(user, this);

    if (!items.has('cover')) return null;

    const button = items.get('cover');

    // Icon-only: the cover has no room for a label, and the button's own
    // tooltip already names the action.
    button.attrs = {
      ...button.attrs,
      className: classList(button.attrs.className, 'Button Button--icon ProfileRedesign-coverButton'),
      title: app.translator.trans('sycho-profile-cover.forum.cover'),
    };
    button.children = [];

    return button;
  }

  badges(user) {
    const badges = user.badges().toArray();

    if (!badges.length) return null;

    return <ul className="ProfileRedesign-badges badges">{listItems(badges)}</ul>;
  }

  /**
   * The bio block, lifted out of the meta list.
   *
   * fof/user-bio contributes a full `UserBio` component (formatting, inline
   * editing, permissions) to `UserCard.infoItems` at priority -100. Rendering
   * `user.bio()` as raw text here instead would lose all of that and duplicate
   * the entry, so the extension's own item is pulled out of the list and given
   * its own row under the name.
   */
  bio(items) {
    if (!items.has('bio')) return null;

    const bio = items.get('bio');

    // Remove it so it is not rendered twice — once here, once in the meta line.
    items.remove('bio');

    return <div className="ProfileRedesign-bio">{bio}</div>;
  }

  /**
   * The meta line under the name.
   *
   * Built by borrowing `UserCard.prototype.infoItems`, which is where core puts
   * "last seen"/"joined" and — importantly — where extensions hook to add their
   * own profile details (our own uinfo child-info line among them). Calling it
   * with `this` as the UserCard keeps that whole extend chain working even
   * though no UserCard is rendered here; rebuilding the list by hand would drop
   * every extension's contribution silently.
   */
  metaItems(user) {
    let items;

    try {
      // fof/user-bio reads `this.attrs.editable` to decide whether the bio is
      // editable inline, so the call needs the same attrs a UserCard would have.
      items = UserCard.prototype.infoItems.call(this.userCardContext({ user, editable: this.attrs.editable }));
    } catch (e) {
      // One extension's extender throwing takes down the whole chain, and with
      // it every other extension's entry. Surface it instead of failing mute —
      // the profile still renders, but the cause is visible.
      console.error('[profile-redesign] UserCard.infoItems threw; profile details may be missing.', e);

      items = new ItemList();
    }

    // The likes total is shown in the stats strip below, so drop the duplicate
    // line clarkwinkelmann/likes-received adds here. The follower/following line
    // ianm/follow-users contributes is deliberately kept.
    items.remove('likesReceived');

    // Core's own entries, restyled. Replacing them by key keeps the priorities
    // (and therefore the ordering against extension items) that core assigned.
    const lastSeenAt = user.lastSeenAt();

    if (lastSeenAt && items.has('lastSeen')) {
      const online = user.isOnline();

      items.setContent(
        'lastSeen',
        <span className={classList('ProfileRedesign-lastSeen', { online })}>
          {online
            ? [icon('fas fa-circle'), ' ', app.translator.trans('core.forum.user.online_text')]
            : [icon('far fa-clock'), ' ', humanTime(lastSeenAt)]}
        </span>
      );
    }

    const joinTime = user.joinTime();

    if (joinTime && items.has('joined')) {
      items.setContent(
        'joined',
        <span className="ProfileRedesign-joined">
          {icon('far fa-calendar')} {app.translator.trans('core.forum.user.joined_date_text', { ago: humanTime(joinTime) })}
        </span>
      );
    }

    return items;
  }
}
